import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  RotateCcw,
  X
} from 'lucide-react';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const toYmd = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const parseYmd = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDisplayDate = (value, locale = 'en-GB') => {
  const date = parseYmd(value);
  if (!date) return '';
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

/**
 * Date + Status (+ optional Collateral) filter bar:
 * Date popover applies on Apply; status/collateral apply immediately on change.
 */
const LoanFilterBar = ({
  t,
  language = 'en',
  statusOptions,
  getStatusOptionLabel,
  collateralOptions,
  getCollateralOptionLabel,
  dateMode,
  filterDate,
  fromDate,
  toDate,
  filterMonth,
  filterYear,
  statusFilter,
  collateralFilter,
  onApply,
  onReset
}) => {
  const locale = language === 'hi' ? 'hi-IN' : 'en-GB';
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Date drafts edited until Apply is clicked
  const [draftMode, setDraftMode] = useState(dateMode || 'date');
  const [draftDate, setDraftDate] = useState(filterDate || '');
  const [draftFrom, setDraftFrom] = useState(fromDate || '');
  const [draftTo, setDraftTo] = useState(toDate || '');
  const [draftMonth, setDraftMonth] = useState(filterMonth || '');
  const [draftYear, setDraftYear] = useState(filterYear || '');
  const [viewMonth, setViewMonth] = useState(() => {
    const seed = parseYmd(filterDate || fromDate) || new Date();
    return startOfMonth(seed);
  });

  useEffect(() => {
    setDraftMode(dateMode || 'date');
    setDraftDate(filterDate || '');
    setDraftFrom(fromDate || '');
    setDraftTo(toDate || '');
    setDraftMonth(filterMonth || '');
    setDraftYear(filterYear || '');
  }, [dateMode, filterDate, fromDate, toDate, filterMonth, filterYear]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => current - i);
  }, []);

  const dateTriggerLabel = useMemo(() => {
    if (dateMode === 'date' && filterDate) {
      return formatDisplayDate(filterDate, locale);
    }
    if (dateMode === 'range' && (fromDate || toDate)) {
      const a = formatDisplayDate(fromDate, locale) || '…';
      const b = formatDisplayDate(toDate, locale) || '…';
      return `${a} – ${b}`;
    }
    if (dateMode === 'month' && filterMonth) {
      const [y, m] = filterMonth.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    if (dateMode === 'year' && filterYear) {
      return String(filterYear);
    }
    return t('loans.dateFieldLabel') || 'Date';
  }, [dateMode, filterDate, fromDate, toDate, filterMonth, filterYear, locale, t]);

  const tipText = useMemo(() => {
    if (draftMode === 'range') return t('loans.dateTips.range');
    if (draftMode === 'month') return t('loans.dateTips.month');
    if (draftMode === 'year') return t('loans.dateTips.year');
    return t('loans.dateTips.single');
  }, [draftMode, t]);

  const calendarDays = useMemo(() => {
    const first = startOfMonth(viewMonth);
    const startOffset = first.getDay();
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const prevMonthDays = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 0).getDate();
    const cells = [];

    for (let i = startOffset - 1; i >= 0; i -= 1) {
      const day = prevMonthDays - i;
      const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, day);
      cells.push({ date, ymd: toYmd(date), outside: true });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
      cells.push({ date, ymd: toYmd(date), outside: false });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const nextIndex = cells.length - (startOffset + daysInMonth) + 1;
      const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, nextIndex);
      cells.push({ date, ymd: toYmd(date), outside: true });
    }
    return cells;
  }, [viewMonth]);

  const modeButtons = [
    { id: 'date', label: t('loans.dateFilterOptions.date'), icon: Calendar },
    { id: 'range', label: t('loans.dateFilterOptions.range'), icon: CalendarRange },
    { id: 'month', label: t('loans.dateFilterOptions.month'), icon: CalendarDays },
    { id: 'year', label: t('loans.dateFilterOptions.year'), icon: CalendarDays }
  ];

  const clearDraftDates = () => {
    setDraftDate('');
    setDraftFrom('');
    setDraftTo('');
    setDraftMonth('');
    setDraftYear('');
  };

  const selectToday = () => {
    const today = toYmd(new Date());
    setViewMonth(startOfMonth(new Date()));
    if (draftMode === 'date') setDraftDate(today);
    else if (draftMode === 'range') {
      setDraftFrom(today);
      setDraftTo(today);
    } else if (draftMode === 'month') {
      const now = new Date();
      setDraftMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    } else if (draftMode === 'year') {
      setDraftYear(String(new Date().getFullYear()));
    }
  };

  const onDayClick = (ymd, outside) => {
    if (outside) {
      const d = parseYmd(ymd);
      if (d) setViewMonth(startOfMonth(d));
    }
    if (draftMode === 'date') {
      setDraftDate(ymd);
      return;
    }
    if (draftMode === 'range') {
      if (!draftFrom || (draftFrom && draftTo)) {
        setDraftFrom(ymd);
        setDraftTo('');
        return;
      }
      if (ymd < draftFrom) {
        setDraftTo(draftFrom);
        setDraftFrom(ymd);
      } else {
        setDraftTo(ymd);
      }
    }
  };

  const isSelected = (ymd) => {
    if (draftMode === 'date') return draftDate === ymd;
    if (draftMode === 'range') {
      if (draftFrom && draftTo) return ymd >= draftFrom && ymd <= draftTo;
      return ymd === draftFrom;
    }
    return false;
  };

  const isRangeEdge = (ymd) => draftMode === 'range' && (ymd === draftFrom || ymd === draftTo);

  const currentCollateral =
    collateralFilter != null
      ? collateralFilter
      : (collateralOptions?.[0] || 'All');

  const emitApply = (overrides = {}) => {
    onApply({
      dateMode: overrides.dateMode ?? dateMode ?? 'date',
      filterDate: overrides.filterDate ?? filterDate ?? '',
      fromDate: overrides.fromDate ?? fromDate ?? '',
      toDate: overrides.toDate ?? toDate ?? '',
      filterMonth: overrides.filterMonth ?? filterMonth ?? '',
      filterYear: overrides.filterYear ?? filterYear ?? '',
      statusFilter: overrides.statusFilter ?? statusFilter ?? statusOptions?.[0] ?? 'All',
      collateralStatusFilter: overrides.collateralStatusFilter ?? currentCollateral
    });
  };

  const handleDateApply = () => {
    emitApply({
      dateMode: draftMode,
      filterDate: draftDate,
      fromDate: draftFrom,
      toDate: draftTo,
      filterMonth: draftMonth,
      filterYear: draftYear
    });
    setOpen(false);
  };

  const handleReset = () => {
    clearDraftDates();
    setDraftMode('date');
    onReset();
    setOpen(false);
  };

  const handleStatusChange = (value) => {
    emitApply({ statusFilter: value });
  };

  const handleCollateralChange = (value) => {
    emitApply({ collateralStatusFilter: value });
  };

  const formatStatusOption = (option) => {
    const label =
      option === 'All' || option === 'ALL'
        ? (getStatusOptionLabel(option) || t('loans.statusOptions.all') || 'All')
        : getStatusOptionLabel(option);
    return `${t('loans.statusFilterLabel') || 'Status'}: ${label}`;
  };

  const monthLabel = viewMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  return (
    <div className="loan-filter-bar" ref={wrapRef}>
      <div className="loan-filter-date-wrap">
        <button
          type="button"
          className={`loan-filter-field${open ? ' is-open' : ''}`}
          onClick={() => setOpen((v) => !v)}
        >
          <Calendar size={16} className="loan-filter-field-icon" />
          <span className="loan-filter-field-text">{dateTriggerLabel}</span>
          <ChevronDown size={16} className="loan-filter-field-chevron" />
        </button>

        {open && (
          <>
            <button
              type="button"
              className="loan-date-backdrop"
              aria-label={t('loans.closeDateFilter') || 'Close'}
              onClick={() => setOpen(false)}
            />
            <div className="loan-date-popover" role="dialog" aria-label={t('loans.dateFilterLabel')}>
              <div className="loan-date-popover-top">
                <span className="loan-date-popover-heading">{t('loans.dateFilterLabel') || 'Date filter'}</span>
                <button
                  type="button"
                  className="loan-date-popover-close"
                  onClick={() => setOpen(false)}
                  aria-label={t('loans.closeDateFilter') || 'Close'}
                >
                  <X size={18} />
                </button>
              </div>

              <aside className="loan-date-popover-sidebar">
                {modeButtons.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`loan-date-mode-btn${draftMode === id ? ' is-active' : ''}`}
                    onClick={() => setDraftMode(id)}
                  >
                    <Icon size={16} />
                    <span>{label}</span>
                  </button>
                ))}
                <div className="loan-date-tip">
                  <Lightbulb size={14} />
                  <span>{tipText}</span>
                </div>
              </aside>

              <div className="loan-date-popover-main">
                {(draftMode === 'date' || draftMode === 'range') && (
                  <>
                    <div className="loan-cal-header">
                      <button type="button" className="loan-cal-nav" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} aria-label="Previous month">
                        <ChevronLeft size={18} />
                      </button>
                      <span className="loan-cal-title">{monthLabel}</span>
                      <button type="button" className="loan-cal-nav" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} aria-label="Next month">
                        <ChevronRight size={18} />
                      </button>
                    </div>
                    <div className="loan-cal-weekdays">
                      {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
                    </div>
                    <div className="loan-cal-grid">
                      {calendarDays.map((cell) => (
                        <button
                          key={cell.ymd + String(cell.outside)}
                          type="button"
                          className={[
                            'loan-cal-day',
                            cell.outside ? 'is-outside' : '',
                            isSelected(cell.ymd) ? 'is-selected' : '',
                            (draftMode === 'date' && draftDate === cell.ymd) || isRangeEdge(cell.ymd) ? 'is-edge' : ''
                          ].filter(Boolean).join(' ')}
                          onClick={() => onDayClick(cell.ymd, cell.outside)}
                        >
                          {cell.date.getDate()}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {draftMode === 'month' && (
                  <div className="loan-date-simple">
                    <label className="loan-date-simple-label">{t('loans.dateFilterOptions.month')}</label>
                    <input
                      type="month"
                      className="loan-date-simple-input"
                      value={draftMonth}
                      onChange={(e) => setDraftMonth(e.target.value)}
                    />
                  </div>
                )}

                {draftMode === 'year' && (
                  <div className="loan-date-simple">
                    <label className="loan-date-simple-label">{t('loans.dateFilterOptions.year')}</label>
                    <select
                      className="loan-date-simple-input"
                      value={draftYear}
                      onChange={(e) => setDraftYear(e.target.value)}
                    >
                      <option value="">{t('loans.dateFilterOptions.year')}</option>
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="loan-cal-footer">
                  <button type="button" className="loan-cal-clear" onClick={clearDraftDates}>
                    {t('loans.clearDateFilter')}
                  </button>
                  <div className="loan-cal-footer-actions">
                    <button type="button" className="loan-cal-today" onClick={selectToday}>
                      {t('loans.todayLabel')}
                    </button>
                    <button type="button" className="loan-cal-done" onClick={handleDateApply}>
                      <Check size={14} />
                      {t('common.apply') || 'Apply'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <select
        className="loan-filter-field loan-filter-select loan-filter-select--status"
        value={statusFilter || statusOptions?.[0] || 'All'}
        onChange={(e) => handleStatusChange(e.target.value)}
        aria-label={t('loans.statusFilterLabel')}
      >
        {statusOptions.map((option) => (
          <option key={option} value={option}>
            {formatStatusOption(option)}
          </option>
        ))}
      </select>

      {Array.isArray(collateralOptions) && collateralOptions.length > 0 && (
        <select
          className="loan-filter-field loan-filter-select loan-filter-select--collateral"
          value={currentCollateral}
          onChange={(e) => handleCollateralChange(e.target.value)}
          aria-label={t('loans.collateralFilterLabel')}
        >
          {collateralOptions.map((option) => (
            <option key={option} value={option}>
              {`${t('loans.collateralFilterLabel')}: ${
                getCollateralOptionLabel
                  ? getCollateralOptionLabel(option)
                  : option
              }`}
            </option>
          ))}
        </select>
      )}

      <button type="button" className="loan-filter-btn loan-filter-btn--ghost" onClick={handleReset}>
        <RotateCcw size={16} />
        {t('loans.resetAction') || 'Reset'}
      </button>
    </div>
  );
};

export default LoanFilterBar;
export { toYmd, formatDisplayDate };
